/**
 * A porta serial do navegador (Web Serial), reduzida ao que o provisionamento
 * usa: ler linhas e escrever linhas. Os tipos são declarados aqui, e não por
 * `@types/w3c-web-serial`, porque o fluxo depende de três membros da porta e
 * um pacote inteiro de tipos para isso seria dependência sem retorno.
 *
 * NADA do que passa por aqui é logado: a linha escrita carrega a credencial e
 * a senha do Wi-Fi.
 */

export interface PortaSerial {
  open(opcoes: { baudRate: number }): Promise<void>
  close(): Promise<void>
  readable: ReadableStream<Uint8Array> | null
  writable: WritableStream<Uint8Array> | null
  setSignals?(sinais: { dataTerminalReady?: boolean; requestToSend?: boolean }): Promise<void>
  /** `false` depois que o cabo sai. Chrome 124+; sem o campo, não se sabe. */
  readonly connected?: boolean
  /** Fabricante e produto USB da ponte serial. Só o diagnóstico usa. */
  getInfo?(): { usbVendorId?: number; usbProductId?: number }
}

/** O filtro do seletor de porta do navegador (Web Serial `SerialPortFilter`). */
export type FiltroDePorta = { usbVendorId?: number; usbProductId?: number }

type EventoDePorta = { target: unknown }

type SerialDoNavegador = {
  requestPort(opcoes?: { filters?: FiltroDePorta[] }): Promise<PortaSerial>
  /** Só as portas que ESTE navegador já autorizou; nenhuma abre o seletor. */
  getPorts?(): Promise<PortaSerial[]>
  addEventListener?(tipo: 'connect' | 'disconnect', ouvinte: (e: EventoDePorta) => void): void
  removeEventListener?(tipo: 'connect' | 'disconnect', ouvinte: (e: EventoDePorta) => void): void
}

const serialDe = (nav: Navigator) => (nav as unknown as { serial: SerialDoNavegador }).serial

/**
 * As pontes USB-serial das placas da frota: CP2102 (Silicon Labs, 0x10c4) no
 * Robô I e CH9102 (WCH, 0x1a86) no Robô II. Sem filtro, o seletor do Chrome no
 * Linux lista `ttyS0` a `ttyS31` antes do robô — medido em 24/09/2026 no Robô I.
 * Placa com outra ponte não aparece: acrescentar o fabricante aqui.
 */
export const FILTROS_DO_ROBO: readonly FiltroDePorta[] = [
  { usbVendorId: 0x10c4 },
  { usbVendorId: 0x1a86 },
]

export function temWebSerial(nav: Navigator = navigator): boolean {
  return 'serial' in nav
}

/** Tem de ser chamada DENTRO do clique: o navegador exige gesto do usuário. */
export function pedirPorta(nav: Navigator = navigator): Promise<PortaSerial> {
  return serialDe(nav).requestPort({
    filters: [...FILTROS_DO_ROBO],
  })
}

/** Passa em `FILTROS_DO_ROBO`? Sem `getInfo`, não se sabe, e a porta não conta. */
export function eDaFrota(porta: PortaSerial): boolean {
  const info = porta.getInfo?.()
  return !!info && FILTROS_DO_ROBO.some((f) => f.usbVendorId === info.usbVendorId)
}

/**
 * As portas já autorizadas que são de robô (spec 2026-09-24-robo-painel-de-
 * ajustes, 3.1, passo 1). Não abre nenhuma: quem abre decide, porque abrir
 * reinicia a placa CP2102 do Robô I.
 */
export async function portasAutorizadasDoRobo(nav: Navigator = navigator): Promise<PortaSerial[]> {
  const serial = serialDe(nav)
  if (!serial?.getPorts) return []
  try {
    return (await serial.getPorts()).filter(eDaFrota)
  } catch {
    return []
  }
}

/**
 * `connect`/`disconnect` do Web Serial (spec 3.4). Devolve quem desliga. O
 * `target` do evento é a porta.
 */
export function ouvirPortas(
  aoConectar: (p: PortaSerial) => void, aoDesconectar: (p: PortaSerial) => void,
  nav: Navigator = navigator,
): () => void {
  const serial = serialDe(nav)
  if (!serial?.addEventListener) return () => {}
  const conectou = (e: EventoDePorta) => aoConectar(e.target as PortaSerial)
  const desconectou = (e: EventoDePorta) => aoDesconectar(e.target as PortaSerial)
  serial.addEventListener('connect', conectou)
  serial.addEventListener('disconnect', desconectou)
  return () => {
    serial.removeEventListener?.('connect', conectou)
    serial.removeEventListener?.('disconnect', desconectou)
  }
}

/** Leitura de linhas com prazo. `proxima` devolve `null` quando o prazo vence. */
export interface Linhas {
  proxima(prazoMs: number): Promise<string | null>
  escrever(linha: string): Promise<void>
  fechar(): Promise<void>
}

/**
 * `aoErroDeLeitura` recebe o erro que derruba o laço de leitura. Sem ele o erro
 * some: quem espera só vê o prazo vencer, e "a USB caiu" (`NetworkError: The
 * device has been lost`) fica igual a "o robô não respondeu".
 */
export type OpcoesDasLinhas = {
  /**
   * Vê TODA linha lida, antes da fila — é o que alimenta o log ao vivo. Recebe a
   * linha crua: quem mostra redige (`robo-ajustes/redacao.ts`).
   */
  aoLer?: (linha: string) => void
  /**
   * Teto da fila de linhas que ninguém pediu. Com a porta aberta por horas (a
   * tela de Ajustes), sem teto ela cresceria a cada `RESOURCES:`. Sem valor,
   * sem teto — o comportamento de antes.
   */
  teto?: number
}

export async function abrirLinhas(
  porta: PortaSerial, baudRate = 115200, aoErroDeLeitura?: (erro: unknown) => void,
  opcoes: OpcoesDasLinhas = {},
): Promise<Linhas> {
  // A porta pode ter ficado aberta nesta página: o cabo saiu no meio de uma escrita,
  // o `close()` recusou por causa do escritor preso, e o `open()` seguinte responde
  // `InvalidStateError` para sempre — a tela dizia "a placa não respondeu" até
  // recarregar (01/10/2026, Izy, TASK-12152). Fecha de vez o que esta página abriu.
  await soltarPortaPresa(porta)
  await porta.open({ baudRate })
  abertaNestaPagina.add(porta)
  // O Linux liga DTR e RTS ao abrir, e o navegador não mexe neles. No Robô II
  // (CH9102) RTS ligado segura o ESP32 em reset: a placa fica muda enquanto a
  // porta estiver aberta (5 de 8 aberturas, medido em 24/09/2026). Desligar os
  // dois reinicia a placa, e quem chama já espera o `LIVE:` do boot.
  await porta.setSignals?.({ dataTerminalReady: false, requestToSend: false })
  const fila: string[] = []
  const esperando: Array<(l: string | null) => void> = []
  let resto = ''
  let fechada = false
  let escritor: WritableStreamDefaultWriter<Uint8Array> | null = null
  const decoder = new TextDecoder()
  const leitor = porta.readable!.getReader()

  const entregar = (linha: string) => {
    try { opcoes.aoLer?.(linha) } catch { /* o log nunca derruba a leitura */ }
    const quem = esperando.shift()
    if (quem) quem(linha)
    else {
      fila.push(linha)
      if (opcoes.teto !== undefined && fila.length > opcoes.teto) fila.shift()
    }
  }

  // Laço de leitura: quebra em `\n`, descarta `\r` (o firmware manda `\r\n`).
  void (async () => {
    try {
      for (;;) {
        const { value, done } = await leitor.read()
        if (done) break
        resto += decoder.decode(value, { stream: true })
        let i
        while ((i = resto.indexOf('\n')) >= 0) {
          entregar(resto.slice(0, i).replace(/\r$/, ''))
          resto = resto.slice(i + 1)
        }
      }
    } catch (erro) {
      // Porta arrancada ou fechada no meio: quem espera recebe null no prazo.
      if (!fechada) aoErroDeLeitura?.(erro)
    }
  })()

  const linhas: Linhas = {
    proxima(prazoMs) {
      const pronta = fila.shift()
      if (pronta !== undefined) return Promise.resolve(pronta)
      return new Promise((resolve) => {
        const t = setTimeout(() => {
          const i = esperando.indexOf(entregarUma)
          if (i >= 0) esperando.splice(i, 1)
          resolve(null)
        }, prazoMs)
        function entregarUma(l: string | null) { clearTimeout(t); resolve(l) }
        esperando.push(entregarUma)
      })
    },
    async escrever(linha) {
      const w = porta.writable!.getWriter()
      escritor = w
      try {
        await w.write(new TextEncoder().encode(linha + '\n'))
      } finally {
        if (escritor === w) escritor = null
        try { w.releaseLock() } catch { /* o fechar ja' abortou */ }
      }
    },
    async fechar() {
      if (fechada) return
      fechada = true
      if (abertasNestaPagina.get(porta) === linhas) abertasNestaPagina.delete(porta)
      try { await leitor.cancel() } catch { /* ja' fechada */ }
      try { leitor.releaseLock() } catch { /* idem */ }
      // Escrita pendurada (cabo arrancado no meio) segura a trava do `writable`, e
      // com ela o `close()` recusa e a porta fica aberta.
      const w = escritor
      escritor = null
      if (w) {
        // Sem `await`: o abort espera a escrita em curso assentar, e a pendurada
        // nunca assenta. Soltar a trava ja' basta para o `close()`.
        w.abort().catch(() => { /* ja' abortada */ })
        try { w.releaseLock() } catch { /* idem */ }
      }
      try { await porta.close(); abertaNestaPagina.delete(porta) } catch { /* idem */ }
    },
  }
  abertasNestaPagina.set(porta, linhas)
  return linhas
}

/** As linhas abertas por esta página, por porta: o que `soltarPortaPresa` sabe fechar. */
const abertasNestaPagina = new WeakMap<PortaSerial, Linhas>()
/** Porta que `abrirLinhas` abriu e nenhum `close()` fechou (ex.: `setSignals` lançou). */
const abertaNestaPagina = new WeakSet<PortaSerial>()

/**
 * Antes de abrir: se esta página ainda tem linhas abertas nesta porta, fecha-as
 * (leitor, escritor e porta); se a porta que ela abriu segue aberta, tenta o `close()`. O que
 * outra aba abriu nao se solta daqui — ai' o `open()` falha com `InvalidStateError`
 * e `portaPresa(erro)` deixa a tela dizer isso.
 */
async function soltarPortaPresa(porta: PortaSerial): Promise<void> {
  const velhas = abertasNestaPagina.get(porta)
  if (velhas) await velhas.fechar()
  if (abertaNestaPagina.has(porta)) {
    try { await porta.close(); abertaNestaPagina.delete(porta) } catch { /* travada */ }
  }
}

/** O `open()` recusou porque a porta ja' esta' aberta (outra aba, ou travada). */
export function portaPresa(erro: unknown): boolean {
  return (erro as { name?: unknown } | null)?.name === 'InvalidStateError'
}
